import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowDown, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useT } from '../i18n/I18nProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_HERO = {
  badge: 'EST. 1979 • KUALA LUMPUR, MALAYSIA',
  title_line1: 'Our Society,',
  title_line2: 'Our Responsibility.',
  description:
    'For over four decades, EWRF has empowered underserved communities through education, counselling, and welfare – building brighter futures, one life at a time.',
  highlight_words: ['education', 'counselling', 'welfare'],
  primary_cta_label: 'EXPLORE OUR PROGRAMMES',
  primary_cta_target: 'programmes',
  secondary_cta_label: '♥ DONATE NOW',
  secondary_cta_target: 'donate',
};

// Render description with highlighted words in gold
const renderDescription = (text, highlightWords = []) => {
  if (!text) return null;
  const words = (highlightWords || []).filter(Boolean);
  if (!words.length) return text;
  const pattern = new RegExp(`\\b(${words.map((w) => w.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')).join('|')})\\b`, 'gi');
  const parts = text.split(pattern);
  return parts.map((p, i) =>
    words.some((w) => w.toLowerCase() === p.toLowerCase()) ? (
      <span key={i} className="font-semibold text-amber-200">{p}</span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    ),
  );
};

const DEFAULT_HERO_SLIDES = [
  { src: '/api/uploads/hero_slide1_golf_poster.jpeg', title: 'EWRF Charity Golf 2026', subtitle: 'Hit the green, lift a life · 18 Sept · Kelab Golf Negara Subang · Register via QR', fit: 'contain' },
  { src: '/api/uploads/slider_multicultural_kids.jpg', title: 'Unity in Diversity', subtitle: 'Celebrating Malaysia\u2019s multicultural children through our programmes', fit: 'contain' },
  { src: '/api/uploads/slider_csk_team.jpg', title: 'Champions of CSK', subtitle: 'Empowering young girls through sports, teamwork, and mentorship', fit: 'contain' },
  { src: '/api/uploads/history_phase3_2019_gathering.jpg', title: '47 Years of Impact', subtitle: 'Serving Malaysian communities since 1979 with heart and purpose', fit: 'contain' },
  { src: '/api/uploads/hero_slide5_students.png', title: 'The Faces of EWRF', subtitle: 'Educational, Welfare & Research Foundation Malaysia · 47 Years of nurturing tomorrow\u2019s leaders', fit: 'cover' },
];

const Hero = () => {
  const t = useT();
  const [slide, setSlide] = useState(0);
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [slides, setSlides] = useState(DEFAULT_HERO_SLIDES);
  const [intervalMs, setIntervalMs] = useState(9000);

  useEffect(() => {
    if (!slides.length) return;
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % slides.length);
    }, intervalMs || 9000);
    return () => clearInterval(id);
  }, [slides.length, intervalMs]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/content/hero`);
        if (res.data) setHero({ ...DEFAULT_HERO, ...res.data });
      } catch (e) { /* keep defaults */ }
    })();
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/content/hero-slides`);
        const arr = (res.data?.slides || []).filter((s) => s.src);
        if (arr.length) setSlides(arr);
        if (res.data?.interval_ms) setIntervalMs(res.data.interval_ms);
      } catch (e) { /* keep defaults */ }
    })();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: '47+', label: t('hero.stat.years'),      accent: 'from-amber-400 to-orange-500' },
    { value: '15K+', label: t('hero.stat.lives'),     accent: 'from-rose-400 to-pink-500' },
    { value: '500+', label: t('hero.stat.volunteers'), accent: 'from-emerald-400 to-teal-500' },
    { value: '120+', label: t('hero.stat.schools'),   accent: 'from-violet-400 to-fuchsia-500' },
  ];

  return (
    <>
      {/* Clean image slider with animated caption overlay */}
      <section className="relative w-full h-[45vh] md:h-[65vh] overflow-hidden bg-[#0A1628]" data-testid="hero-slider">
        {slides.map((s, i) => {
          const fit = s.fit || 'cover';
          return (
            <div
              key={`${s.src}-${i}`}
              className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${
                i === (slide % slides.length) ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `url(${s.src})`,
                backgroundSize: fit,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: fit === 'contain' ? '#0A1628' : undefined,
              }}
              data-testid={`hero-slide-${i}`}
            />
          );
        })}

        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-20 flex items-center gap-2" data-testid="hero-pagination">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`transition-all rounded-full ${
                i === (slide % slides.length) ? 'w-8 h-2 bg-white shadow' : 'w-2 h-2 bg-white/40 hover:bg-white/70'
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </section>

      {/* Caption strip — sits BELOW the slider so it never covers the image */}
      <div className="bg-[#0A1628] border-t border-white/10" data-testid="hero-caption-strip">
        <div
          key={slide}
          className="max-w-7xl mx-auto px-4 md:px-10 py-4 md:py-5"
          data-testid={`hero-caption-${slide % Math.max(slides.length, 1)}`}
        >
          <div className="text-white text-xl md:text-3xl lg:text-4xl font-serif font-bold animate-[fadeUp_800ms_ease-out_forwards] opacity-0">
            {slides[slide % Math.max(slides.length, 1)]?.title}
          </div>
          <div className="text-white/85 text-sm md:text-base lg:text-lg mt-1 md:mt-2 animate-[fadeUp_800ms_ease-out_200ms_forwards] opacity-0 max-w-3xl">
            {slides[slide % Math.max(slides.length, 1)]?.subtitle}
          </div>
        </div>
      </div>

      {/* Content block below the slider (relocated from overlay) */}
      <section
        className="relative bg-gradient-to-br from-[#0A1628] via-[#0D2B5E] to-[#1565C0] py-16 md:py-24 overflow-hidden"
        data-testid="hero-content"
      >
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-amber-400/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <Badge className="mb-6 bg-white/15 border border-white/40 text-white px-4 py-2 text-xs uppercase tracking-widest backdrop-blur-md inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                {hero.badge}
              </Badge>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-white mb-6 leading-[1.05]">
                {hero.title_line1}<br />
                <span className="italic bg-gradient-to-r from-amber-300 via-orange-300 to-amber-200 bg-clip-text text-transparent">
                  {hero.title_line2}
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/95 mb-8 leading-relaxed max-w-xl">
                {renderDescription(hero.description, hero.highlight_words)}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  onClick={() => scrollToSection(hero.primary_cta_target || 'programmes')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-6 text-base font-bold tracking-wide transition-all w-full sm:w-auto shadow-2xl hover:scale-105"
                  data-testid="hero-programmes-btn"
                >
                  {hero.primary_cta_label || t('hero.cta.programmes')}
                </Button>
                <Button
                  onClick={() => scrollToSection(hero.secondary_cta_target || 'donate')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1565C0] backdrop-blur-md px-8 py-6 text-base font-bold tracking-wide transition-all w-full sm:w-auto"
                  data-testid="hero-donate-btn"
                >
                  {hero.secondary_cta_label || t('hero.cta.donate')}
                </Button>
              </div>

              <button
                onClick={() => scrollToSection('about')}
                className="text-white/70 hover:text-white text-sm inline-flex items-center gap-2 transition-colors"
              >
                {t('hero.scroll_more')} <ArrowDown className="w-4 h-4 animate-bounce" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className="relative bg-white/10 backdrop-blur-xl border border-white/30 rounded-2xl p-6 md:p-8 text-center shadow-2xl hover:scale-105 transition-transform group overflow-hidden"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-0 group-hover:opacity-20 transition-opacity`}></div>
                  <div className={`text-4xl md:text-6xl font-black mb-2 bg-gradient-to-br ${s.accent} bg-clip-text text-transparent`}>
                    {s.value}
                  </div>
                  <div className="text-xs md:text-sm text-white/95 font-medium uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
